import React, { useEffect } from "react";
import Loader from "../Loader.tsx";
import {
  getContractIMSS,
  getContractPdf,
} from "../../services/colaborador.pdf.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import ModalContainer from "../ModalContainer.tsx";
import { Partner } from "../../models/Partner.ts";
import UploadContract from "./UploadContract.tsx";
import { Contract } from "../../models/Contract.ts";
import { useReactToPrint } from "react-to-print";
import {
  getFileById,
  getFilePreviewById,
} from "../../services/proceedings.service.ts";
import { IContract } from "../../interfaces/others/IContract.ts";
import ContractIMSSPrint from "../../pages/ContractIMSSPrint.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { fetchContractByCandidate } from "../../services/contract.service.ts";
import SaveImssDate from "./SaveImssDate.tsx";
import { useDownloadContractIMSSDocx } from "../../hooks/useDownloadContractIMSSDocx.ts";
import { updateContractValidity } from "../../services/contract.service.ts";
import moment from "moment";

import { getProfileTabPaneClass } from "./profileTabUtils.ts";

const AccountantContract = ({
  partner,
  isTabActive = false,
  embedded = false,
}: {
  partner: Partner;
  isTabActive?: boolean;
  embedded?: boolean;
}) => {
  const tabPaneClass = getProfileTabPaneClass(isTabActive, embedded);
  const [imssDate, setImssDate] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [contracts, setContracts] = React.useState<Contract[]>([]);
  const [modalUpload, setModalUpload] = React.useState<boolean>(false);
  const [modalFormat, setModalFormat] = React.useState<boolean>(false);
  const [isPress, setIsPress] = React.useState<boolean>(false);
  const [previews, setPreviews] = React.useState<any>([]);
  const [contract, setContract] = React.useState<IContract>({});
  const componentRef = React.useRef() as React.MutableRefObject<any>;
  const { handleDownloadContractIMSSDocx } = useDownloadContractIMSSDocx();
  const [editingValidityId, setEditingValidityId] = React.useState<number | null>(null);
  const [validityDate, setValidityDate] = React.useState<string>("");
  
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @media all {
        .page-break {
          display: none;
        }
      }
      @media print {
        html, body {
          height: initial !important;
          overflow: initial !important;
        }
        .page-break {
          display: block;
          page-break-before: auto;
        }
      }
      @page {
        size: letter;
        margin: 25mm;
      }
    `,
  });

  const generateContract = async (colaboratorId, format: 'pdf' | 'docx' = 'pdf') => {
    setLoading(true);
    try {
      const response = await getContractIMSS(colaboratorId);
      setContract(response);
      
      if (format === 'docx') {
        await handleDownloadContractIMSSDocx(response);
        setLoading(false);
        setModalFormat(false);
        return;
      }
      
      setIsPress(true);
    } catch (error) {
      notification(
        "Error",
        "Ocurrió un error al descargar el contrato",
        "danger"
      );
      setLoading(false);
      setModalFormat(false);
    }
  };

  React.useEffect(() => {
    fetchContracts();
  }, [partner]);

  React.useEffect(() => {
    if (contract?.rfc && isPress) {
      setTimeout(() => {
        handlePrint();
        setLoading(false);
        setModalFormat(false);
        setIsPress(false);
      }, 100);
    }
  }, [contract, isPress]);

  const fetchContracts = async () => {
    try {
      const response = await fetchContractByCandidate(partner.candidate.id);
      setContracts(response);
    } catch (error) {
      notification(
        "Error",
        "Ocurrió un error al obtener los contratos",
        "danger"
      );
      console.log(error, " fetch contract");
    }
  };

  const downloadFile = async (id: string) => {
    setLoading(true);
    try {
      const response = await getFileById(id);
      const url = response.url;
      const link = document.createElement("a");
      link.href = url;
      link.download = "file"; // Aquí puedes poner el nombre del archivo que prefieras
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      notification(
        "Error",
        "Ocurrió un error al descargar el contrato",
        "danger"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contracts.length > 0) {
      Promise.all(
        contracts.map(async (contract) => {
          if (contract.file.id === "f0b6fe93-002a-4c04-9ce4-b1e0f79e5228") {
            return null;
          }
          try {
            const response = await getFilePreviewById(contract.file.id);
            return { ...response, contractId: contract.id, validityDate: contract.validityDate };
          } catch (error) {
            notification(
              "Error",
              "Ocurrió un error al obtener los contratos",
              "danger"
            );
            console.log(error, " fetch contract");
          }
        })
      ).then((values) => {
        const filteredValues = values.filter((value) => value !== null);
        setPreviews(filteredValues);
      });
    }
  }, [contracts]);

  const getMissingFields = (person: any): string[] => {
    const excludedFields = [
      "tipoSangre",
      "tratamiento",
      "alergias",
      "enfermedad",
      "fromPhone",
      "id",
      "createdAt",
      "updateAt",
      "isBanned",
      "schooling",
      "idSchooling",
      "curp",
    ];

    const fieldLabels: { [key: string]: string } = {
      firstName: "Nombre",
      lastName: "Apellido Paterno",
      secondLastName: "Apellido Materno",
      email: "Correo electrónico",
      phone: "Teléfono",
      address: "Domicilio",
      birthdate: "Fecha de nacimiento",
      genere: "Género",
      maritalStatus: "Estado civil",
      rfc: "RFC",
      nss: "Número de Seguro Social",
      idMex: "IDMEX",
      ocr: "OCR",
      idSchooling: "Escolaridad",
    };

    const missingFields: string[] = [];

    for (let key in person) {
      if (!person.hasOwnProperty(key)) continue;
      if (excludedFields.includes(key)) continue;

      if (key === "nss") {
        const nssValue = person[key];
        if (!nssValue || nssValue === "" || nssValue === null) {
          missingFields.push(fieldLabels[key] || key);
        } else {
          const nssDigits = String(nssValue).replace(/\D/g, "");
          if (nssDigits.length !== 10 && nssDigits.length !== 11) {
            missingFields.push(`${fieldLabels[key] || key} (debe tener 10 u 11 dígitos)`);
          }
        }
        continue;
      }

      if (person[key] === "" || person[key] === null || person[key] === undefined) {
        if (fieldLabels[key]) {
          missingFields.push(fieldLabels[key]);
        }
      }
    }

    return missingFields;
  };

  const missingFields = getMissingFields(partner?.person || {});
  const isEmpty = missingFields.length > 0;

  if (isEmpty)
    return (
      <div className={tabPaneClass} id="contratoIMSS" style={{ padding: "0 12px" }}>
        <div style={{
          width: "100%",
          background: "#fff3cd",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          marginTop: "12px",
          minHeight: "400px",
          padding: "20px 24px",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
              <div style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#ffc107",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}>
                <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: "24px", color: "#000" }}></i>
              </div>
              <div>
                <h3 style={{ margin: 0, color: "#856404", fontSize: "20px", fontWeight: "700" }}>
                  Información incompleta
                </h3>
                <p style={{ margin: "2px 0 0 0", color: "#856404", fontSize: "14px" }}>
                  No se puede generar el contrato IMSS en este momento
                </p>
              </div>
            </div>
          <div>
            <p style={{ 
              margin: "0 0 16px 0", 
              color: "#374151", 
              fontSize: "14px",
              lineHeight: "1.5" 
            }}>
              El colaborador no cuenta con información suficiente para generar el contrato IMSS. 
              Por favor, completa los siguientes campos en <strong>Datos generales</strong>:
            </p>
            <div style={{
              background: "rgba(255, 255, 255, 0.6)",
              borderRadius: "8px",
              padding: "16px",
              border: "1px solid rgba(0, 0, 0, 0.1)",
            }}>
                <div style={{ marginBottom: "12px" }}>
                  <strong style={{ color: "#374151", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa-solid fa-list-check" style={{ color: "#ffc107", fontSize: "14px" }}></i>
                    Campos faltantes ({missingFields.length}):
                  </strong>
                </div>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: "20px", 
                  color: "#6b7280", 
                  fontSize: "13px",
                  lineHeight: "1.8",
                  columns: missingFields.length > 4 ? 3 : missingFields.length > 2 ? 2 : 1,
                  columnGap: "24px",
                }}>
                  {missingFields.map((field, index) => (
                    <li key={index} style={{ marginBottom: "4px", breakInside: "avoid" }}>
                      <i className="fa-solid fa-circle-dot" style={{ fontSize: "6px", color: "#ffc107", marginRight: "6px" }}></i>
                      {field}
                    </li>
                  ))}
                </ul>
            </div>
            <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid rgba(0, 0, 0, 0.1)" }}>
              <p style={{ margin: 0, color: "#856404", fontSize: "13px", fontStyle: "italic" }}>
                <i className="fa-solid fa-info-circle me-2" style={{ color: "#856404", fontSize: "12px" }}></i>
                Una vez que completes todos los campos requeridos, podrás generar el contrato IMSS desde esta sección.
              </p>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className={tabPaneClass} id="contratoIMSS">
      <div className="container-fluid">
        <div className="row">
          {/* Columna para el widget */}
          <div className="col-12 col-md-4 col-lg-3 mb-3">
            <a href="#" className="widget-card rounded h-100" data-id="widget">
              <div className="widget-card-cover rounded" />
              <div className="widget-card-content d-flex flex-column justify-content-between h-100">
                <div>
                  <h5 className="fs-12px text-body">
                    <b>Contrato</b>
                  </h5>
                  <h4 className="mb-10px text-black">
                    <b>
                      {!isPress ? "Imprimir" : "Subir"} contrato
                      <br />
                    </b>
                  </h4>
                </div>

                <i
                  className={`fa fa-file-pen text-gray text-opacity-50`}
                  style={{
                    fontSize: "100px",
                    textAlign: "center",
                  }}
                />
                <div
                  className="widget-card-content bottom mt-3"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <button
                    className="btn btn-primary fs-16px"
                    onClick={() => setModalFormat(true)}
                  >
                    <i
                      className={`fa-solid fa-download`}
                      style={{ marginRight: 10 }}
                    />
                    Descargar contrato
                  </button>
                  <button
                    className="btn btn-primary fs-16px"
                    onClick={() => setModalUpload(true)}
                  >
                    <i
                      className={`fa-solid fa-upload`}
                      style={{ marginRight: 10 }}
                    />
                    Subir contrato
                  </button>
                  <button
                    className="btn btn-primary fs-16px"
                    onClick={() => setImssDate(true)}
                  >
                    <i
                      className={`fa-solid fa-calendar`}
                      style={{ marginRight: 10 }}
                    />
                    Fecha IMSS
                  </button>
                </div>
              </div>
            </a>
          </div>

          {/* Columna para las previsualizaciones */}
          <div className="col-12 col-md-8 col-lg-9 card d-flex justify-content-center align-items-center">
            <div
              className="overflow-auto"
              style={{
                width: "100%",
                maxWidth: "100%",
                height: "440px",
              }}
            >
              <ul className="list-unstyled d-flex flex-wrap justify-content-center p-3">
                {previews && previews.length > 0 ? (
                  previews.map((preview) => (
                    <li
                      key={preview.index}
                      className="position-relative me-3 mb-3 d-flex flex-column justify-content-center align-items-center bg-white shadow-sm rounded-2"
                      style={{
                        minWidth: "200px",
                        maxWidth: "250px",
                        minHeight: "400px",
                        padding: "12px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "15px",
                          marginBottom: "10px",
                          textAlign: "center",
                          height: "20px",
                          fontWeight: 600,
                          overflow: "hidden",
                        }}
                      >
                        {preview.name}.{preview.extension}
                      </p>
                      <div className="preview-container">
                        {preview.extension === "pdf" ? (
                          <iframe
                            src={preview.url}
                            title={preview.originalName}
                            style={{ width: "100%", height: "300px" }}
                          ></iframe>
                        ) : (
                          <img
                            src={preview.url}
                            alt={preview.originalName}
                            style={{
                              width: "100%",
                              height: "300px",
                              objectFit: "contain",
                            }}
                          />
                        )}
                      </div>
                      {preview.validityDate && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#5f6368",
                            marginTop: "8px",
                            textAlign: "center",
                            padding: "4px 8px",
                            background: "#f1f3f4",
                            borderRadius: "4px",
                          }}
                        >
                          <i className="fa-solid fa-calendar me-1" />
                          Vigencia: {moment(preview.validityDate).format("DD/MM/YYYY")}
                        </div>
                      )}
                      <div className="d-flex gap-2 mt-2" style={{ width: "100%", padding: "0 8px" }}>
                        <button
                          className="btn btn-sm flex-grow-1"
                          style={{
                            background: "#1a73e8",
                            color: "white",
                            border: "none",
                            fontSize: "12px",
                          }}
                          type="button"
                          onClick={() => {
                            setEditingValidityId(preview.contractId);
                            setValidityDate(
                              preview.validityDate
                                ? moment(preview.validityDate).format("YYYY-MM-DD")
                                : ""
                            );
                          }}
                        >
                          <i className="fa-solid fa-edit me-1" />
                          Editar vigencia
                        </button>
                        <button
                          className="btn btn-amarillo btn-sm"
                          type="button"
                          onClick={() => downloadFile(preview.id)}
                        >
                          <i className="fa-solid fa-cloud-arrow-down"></i>
                        </button>
                      </div>
                    </li>
                  ))
                ) : (
                  <p
                    style={{ fontStyle: "italic" }}
                    className="text-center fs-16px text-gray-500 mt-3"
                  >
                    Aún no hay contratos
                  </p>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Modal */}
        <ModalContainer
          title={`Subir contrato de ${toCamelCase(
            partner.person.firstName +
              " " +
              partner.person.lastName +
              " " +
              partner.person.secondLastName
          )} `}
          open={modalUpload}
          closeFn={() => setModalUpload(false)}
          children={
            <UploadContract
              partner={partner}
              closeFn={() => {
                setModalUpload(false);
                fetchContracts();
              }}
            />
          }
        />
        <ModalContainer
          title="Guardar fecha del contrato IMSS"
          open={imssDate}
          closeFn={() => setImssDate(false)}
          children={
            <SaveImssDate
              partner={partner}
              closeFn={() => {
                setImssDate(false);
                // fetchContracts();
              }}
            />
          }
        />
        <ModalContainer
          title="Seleccionar formato de descarga"
          open={modalFormat}
          closeFn={() => setModalFormat(false)}
          size="sm"
          children={
            <div className="d-flex flex-column gap-3">
              <p className="fs-16px text-center mb-3">
                Selecciona el formato en el que deseas descargar el contrato:
              </p>
              <button
                className="btn btn-primary fs-16px d-flex align-items-center justify-content-center gap-2"
                onClick={() => generateContract(partner.id, 'pdf')}
                disabled={loading}
              >
                <i className="fa-solid fa-file-pdf" />
                Descargar como PDF
              </button>
              <button
                className="btn btn-secondary fs-16px d-flex align-items-center justify-content-center gap-2"
                onClick={() => generateContract(partner.id, 'docx')}
                disabled={loading}
              >
                <i className="fa-solid fa-file-word" />
                Descargar como Word (.docx)
              </button>
            </div>
          }
        />
        <ModalContainer
          title="Editar fecha de vigencia"
          open={editingValidityId !== null}
          closeFn={() => {
            setEditingValidityId(null);
            setValidityDate("");
          }}
          size="sm"
          children={
            <div className="d-flex flex-column gap-3">
              <div>
                <label className="form-label" style={{ fontSize: "14px", fontWeight: 500 }}>
                  Fecha de vigencia
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={validityDate}
                  onChange={(e) => setValidityDate(e.target.value)}
                />
                <small className="text-muted" style={{ fontSize: "12px" }}>
                  Deja vacío para eliminar la fecha de vigencia
                </small>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingValidityId(null);
                    setValidityDate("");
                  }}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!editingValidityId) return;
                    setLoading(true);
                    try {
                      await updateContractValidity(
                        editingValidityId,
                        validityDate || null
                      );
                      notification(
                        "Éxito",
                        "Fecha de vigencia actualizada correctamente",
                        "success"
                      );
                      await fetchContracts();
                      setEditingValidityId(null);
                      setValidityDate("");
                    } catch (error: any) {
                      notification(
                        "Error",
                        error?.response?.data?.message ||
                          "Ocurrió un error al actualizar la fecha de vigencia",
                        "danger"
                      );
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Guardando...
                    </>
                  ) : (
                    "Guardar"
                  )}
                </button>
              </div>
            </div>
          }
        />
      </div>
      <Loader isLoading={loading} />

      <div style={{ display: "none" }}>
        <ContractIMSSPrint ref={componentRef} contractData={contract} />
      </div>
    </div>
  );
};

export default AccountantContract;
